import type { ProjectConfiguration } from "./types";

/* ═══════════════════════════════════════════════════
   Rule-based Scope of Work generation from config
   ═══════════════════════════════════════════════════ */

interface ScopeSection {
  title: string;
  items: string[];
}

const SCOPE_TEMPLATES: Record<string, ScopeSection[]> = {
  "Tub to Shower Conversion": [
    { title: "Demolition", items: ["Remove existing bathtub and surround", "Dispose of old materials", "Inspect subfloor and wall framing"] },
    { title: "Plumbing", items: ["Cap existing tub drain", "Install new shower drain and P-trap", "Relocate or extend supply lines as needed"] },
    { title: "Installation", items: ["Install new shower base", "Install wall system panels", "Apply waterproof membrane and seals"] },
    { title: "Finishing", items: ["Install glass door or enclosure", "Install grab bars and accessories", "Install fixture package (showerhead, valve, handle)"] },
    { title: "Cleanup", items: ["Final cleanup and debris removal", "Customer walkthrough and sign-off"] },
  ],
  "Shower Replacement": [
    { title: "Demolition", items: ["Remove existing shower and surround", "Inspect wall framing and subfloor", "Dispose of old materials"] },
    { title: "Installation", items: ["Install new shower base", "Install wall system panels", "Seal all joints and seams"] },
    { title: "Finishing", items: ["Install glass enclosure", "Install fixtures and accessories", "Install grab bars if selected"] },
    { title: "Cleanup", items: ["Final cleanup and debris removal", "Customer walkthrough and sign-off"] },
  ],
  "Tub Replacement": [
    { title: "Demolition", items: ["Remove existing bathtub", "Inspect plumbing connections", "Dispose of old materials"] },
    { title: "Installation", items: ["Install new bathtub unit", "Connect drain and overflow", "Install wall surround panels"] },
    { title: "Finishing", items: ["Install fixtures and trim", "Caulk all joints", "Install accessories"] },
    { title: "Cleanup", items: ["Final cleanup", "Customer walkthrough and sign-off"] },
  ],
  "Walk-in Tub Installation": [
    { title: "Demolition", items: ["Remove existing tub or shower", "Widen entry area if needed", "Inspect and reinforce subfloor"] },
    { title: "Plumbing", items: ["Install walk-in tub drain assembly", "Connect hot/cold supply lines", "Install inline water heater if required"] },
    { title: "Installation", items: ["Position and level walk-in tub", "Install wall surround panels", "Test door seal and water retention"] },
    { title: "Finishing", items: ["Install grab bars and safety features", "Install fixtures and hand shower", "Complete electrical connections for jets"] },
    { title: "Cleanup", items: ["Final cleanup", "Customer demonstration and walkthrough"] },
  ],
  "Full Bathroom Remodel": [
    { title: "Demolition", items: ["Remove existing tub/shower and surround", "Remove flooring if included", "Remove vanity if included", "Inspect framing, plumbing, and electrical"] },
    { title: "Rough Work", items: ["Complete plumbing rough-in", "Repair or replace subfloor as needed", "Frame any structural modifications"] },
    { title: "Installation", items: ["Install shower/tub base", "Install wall system panels", "Install flooring if included", "Install vanity if included"] },
    { title: "Finishing", items: ["Install glass enclosure", "Install all fixtures and accessories", "Install grab bars", "Paint if included"] },
    { title: "Cleanup", items: ["Final cleanup and debris removal", "Full customer walkthrough and sign-off"] },
  ],
  "Wet Space Only": [
    { title: "Preparation", items: ["Protect surrounding areas", "Remove existing shower/tub area materials"] },
    { title: "Installation", items: ["Install new base", "Install wall panels", "Seal all joints and seams"] },
    { title: "Finishing", items: ["Install fixtures and accessories", "Final inspection"] },
    { title: "Cleanup", items: ["Final cleanup", "Customer sign-off"] },
  ],
};

export function generateScopeOfWork(config: ProjectConfiguration): { sections: ScopeSection[]; summary: string } {
  const sections: ScopeSection[] = [];
  let summary = "";
  const bathroomScopes = config.bathroomScopes ?? [];
  const bathroomPhase2Addons = config.bathroomPhase2Addons ?? [];
  const bathroomMaterials = config.bathroomMaterials ?? [];

  // Bathroom scope-based templates
  if (bathroomScopes.length > 0) {
    const primaryScope = bathroomScopes[0];
    const template = SCOPE_TEMPLATES[primaryScope] || SCOPE_TEMPLATES["Wet Space Only"];
    sections.push(...template.map(s => ({ ...s, items: [...s.items] })));

    // Add Phase 2 items
    if (bathroomPhase2Addons.length > 0) {
      const phase2Items: string[] = [];
      if (bathroomPhase2Addons.includes("Flooring")) phase2Items.push("Remove and replace bathroom flooring");
      if (bathroomPhase2Addons.includes("Vanity")) phase2Items.push("Remove existing vanity and install new vanity");
      if (bathroomPhase2Addons.includes("Toilet")) phase2Items.push("Remove and replace toilet");
      if (bathroomPhase2Addons.includes("Paint")) phase2Items.push("Paint bathroom walls and ceiling");
      if (bathroomPhase2Addons.includes("Window")) phase2Items.push("Replace bathroom window");
      if (phase2Items.length > 0) {
        sections.push({ title: "Phase 2 — Additional Work", items: phase2Items });
      }
    }

    // Add complexity notes
    if (config.plumbingRelocation) {
      const plumbingSection = sections.find(s => s.title === "Plumbing");
      if (plumbingSection) {
        plumbingSection.items.push("Relocate plumbing drain and/or supply lines");
      } else {
        sections.splice(1, 0, { title: "Plumbing", items: ["Relocate plumbing drain and/or supply lines"] });
      }
    }

    if (config.customModifications) {
      sections.splice(1, 0, { title: "Custom Modifications", items: ["Custom framing or structural modifications as specified", "Non-standard layout adjustments"] });
    }

    // Build summary line
    const parts: string[] = [primaryScope];
    if (config.bathroomSystemType) parts.push(`using ${config.bathroomSystemType}`);
    if (bathroomMaterials.length > 0) parts.push(`with ${bathroomMaterials.join(" / ")} materials`);
    if (config.baseWidth && config.baseLength) parts.push(`(${config.baseWidth}" × ${config.baseLength}" base)`);
    summary = parts.join(" ");
  } else if (config.workDescription) {
    summary = config.workDescription;
    sections.push({ title: "Scope of Work", items: [config.workDescription] });
  }

  return { sections, summary };
}
